import { getSocket, joinSocketUser } from "../socket/socket";

const ownerRooms = new Map();
const roomOwners = new Map();

let boundSocket = null;
let boundConnectHandler = null;

const getId = (value) => String(value?._id || value?.id || value || "").trim();

const emitJoin = (chatId) => {
    const socket = ensureSocketBinding();
    if (!socket || !socket.connected || !chatId) return;

    joinSocketUser();
    socket.emit("joinChat", chatId);
};

const emitLeave = (chatId) => {
    const socket = ensureSocketBinding();
    if (!socket || !socket.connected || !chatId) return;

    socket.emit("leaveChat", chatId);
};

const rejoinOwnedRooms = () => {
    const socket = getSocket();
    if (!socket || !socket.connected) return;

    joinSocketUser();

    roomOwners.forEach((owners, chatId) => {
        if (!owners?.size) return;
        socket.emit("joinChat", chatId);
    });
};

function ensureSocketBinding() {
    const socket = getSocket();
    if (!socket) return null;

    if (boundSocket === socket) return socket;

    if (boundSocket && boundConnectHandler) {
        boundSocket.off("connect", boundConnectHandler);
    }

    boundSocket = socket;
    boundConnectHandler = () => {
        rejoinOwnedRooms();
    };

    socket.on("connect", boundConnectHandler);

    return socket;
}

const addOwnerToRoom = (ownerId, chatId) => {
    const cleanOwnerId = String(ownerId || "").trim();
    const cleanChatId = getId(chatId);

    if (!cleanOwnerId || !cleanChatId) return;

    let owners = roomOwners.get(cleanChatId);
    const wasEmpty = !owners || owners.size === 0;

    if (!owners) {
        owners = new Set();
        roomOwners.set(cleanChatId, owners);
    }

    owners.add(cleanOwnerId);

    if (wasEmpty) {
        emitJoin(cleanChatId);
    }
};

const removeOwnerFromRoom = (ownerId, chatId) => {
    const cleanOwnerId = String(ownerId || "").trim();
    const cleanChatId = getId(chatId);

    if (!cleanOwnerId || !cleanChatId) return;

    const owners = roomOwners.get(cleanChatId);
    if (!owners) return;

    owners.delete(cleanOwnerId);

    if (owners.size > 0) return;

    roomOwners.delete(cleanChatId);
    emitLeave(cleanChatId);
};

export const syncChatRoomOwner = (ownerId, chatIds = []) => {
    const cleanOwnerId = String(ownerId || "").trim();
    if (!cleanOwnerId) return;

    ensureSocketBinding();

    const previous = ownerRooms.get(cleanOwnerId) || new Set();
    const next = new Set(
        (Array.isArray(chatIds) ? chatIds : [chatIds])
            .map(getId)
            .filter(Boolean)
    );

    previous.forEach((chatId) => {
        if (next.has(chatId)) return;
        removeOwnerFromRoom(cleanOwnerId, chatId);
    });

    next.forEach((chatId) => {
        if (previous.has(chatId)) return;
        addOwnerToRoom(cleanOwnerId, chatId);
    });

    if (next.size) {
        ownerRooms.set(cleanOwnerId, next);
    } else {
        ownerRooms.delete(cleanOwnerId);
    }
};

export const acquireChatRoom = (chatId, ownerId) => {
    const cleanOwnerId = String(ownerId || "").trim();
    const cleanChatId = getId(chatId);

    if (!cleanOwnerId || !cleanChatId) return;

    const current = ownerRooms.get(cleanOwnerId) || new Set();
    if (current.has(cleanChatId)) return;

    syncChatRoomOwner(cleanOwnerId, [...current, cleanChatId]);
};

export const releaseChatRoom = (chatId, ownerId) => {
    const cleanOwnerId = String(ownerId || "").trim();
    const cleanChatId = getId(chatId);

    if (!cleanOwnerId || !cleanChatId) return;

    const current = ownerRooms.get(cleanOwnerId);
    if (!current?.has(cleanChatId)) return;

    syncChatRoomOwner(
        cleanOwnerId,
        Array.from(current).filter((id) => id !== cleanChatId)
    );
};

export const releaseChatRoomOwner = (ownerId) => {
    syncChatRoomOwner(ownerId, []);
};

export const resyncOwnedChatRooms = () => {
    ensureSocketBinding();
    rejoinOwnedRooms();
};
